import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../../../components/ui/Button';
import DataTable from '../../../components/ui/DataTable';
import DateTimePicker from '../../../components/ui/DateTimePicker';
import GenericSelect from '../../../components/ui/GenericSelect';
import useLayoutBucket from '../../../hooks/useLayoutBucket';
import {
  Field, Fixed, Kpi, KpiLabel, KpiSub, KpiValue, Kpis, Meta, Row, StateBox, TableArea,
} from '../styles';
import {
  enumLabel, extraOptions, fetchRunSummary, fetchRuns, fetchTemplates, formatNumber,
} from '../config';
import { formatDateTime } from '../../../utils/Utils';

// PM analitik P7b — rapor merkezi denetim sekmesi (yalnız İK): her indirme denemesinin kaydı (kim, ne, hangi süzgeç — kişi kimliği
// yok), M58 rapor başarı oranı ve M59 işlem süresi p95. Kayıtlar uygulamadan değiştirilemez (salt okunur). Değişiklik denetimi
// (kalibrasyon, hedef kararları vb.) katalogdaki "Değişiklik Denetimi" şablonundadır.
const EMPTY = { template: null, format: null, status: null, from: '', to: '' };

function toParams(f) {
  const p = {};
  if (f.template) p['template[eq]'] = f.template;
  if (f.format) p['format[eq]'] = f.format;
  if (f.status) p['runStatus[eq]'] = f.status;
  if (f.from) p['createDate[gte]'] = f.from;
  if (f.to) p['createDate[lte]'] = f.to;
  return p;
}

// Süzgeç görüntüsü: dolu anahtarların sayısı + yönetici süzgeci kullanıldıysa işaret (kişi kimliği zaten yazılmaz).
function filterSummary(t, json) {
  try {
    const f = JSON.parse(json || '{}');
    const keys = Object.entries(f).filter(([k, v]) => k !== 'extra' && v != null && v !== false
      && !(Array.isArray(v) && v.length === 0)).map(([k]) => k);
    const extra = Object.keys(f.extra || {});
    return [...keys, ...extra].map((k) => t(`performance.reportCenter.filterKey.${k.replace(/\[.*\]$/, '')}`, { defaultValue: k })).join(', ');
  } catch {
    return '';
  }
}

export default function RunAuditTab() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const bucket = useLayoutBucket();
  const narrow = bucket === 'xs' || bucket === 'sm';
  const [filters, setFilters] = useState(EMPTY);
  const [page, setPage] = useState({ pageNumber: 1, pageSize: 20 });
  const [runs, setRuns] = useState({ loading: true, error: false, data: null });
  const [summary, setSummary] = useState({ loading: true, error: false, data: null });
  const [templates, setTemplates] = useState([]);
  const [attempt, setAttempt] = useState(0);
  const [tableHeight, setTableHeight] = useState(420);
  const tableRef = useRef(null);
  const seq = useRef(0);

  useEffect(() => { fetchTemplates().then(setTemplates).catch(() => setTemplates([])); }, []);

  const params = useMemo(() => toParams(filters), [filters]);
  const key = JSON.stringify(params);
  useEffect(() => { setPage((p) => ({ ...p, pageNumber: 1 })); }, [key]);

  useEffect(() => {
    const mine = ++seq.current;
    setRuns((s) => ({ ...s, loading: true, error: false }));
    fetchRuns({ ...params, pageNumber: page.pageNumber, pageSize: page.pageSize })
      .then((data) => { if (mine === seq.current) setRuns({ loading: false, error: false, data }); })
      .catch(() => { if (mine === seq.current) setRuns({ loading: false, error: true, data: null }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, page.pageNumber, page.pageSize, attempt]);

  useEffect(() => {
    setSummary((s) => ({ ...s, loading: true, error: false }));
    fetchRunSummary(params)
      .then((data) => setSummary({ loading: false, error: false, data }))
      .catch(() => setSummary({ loading: false, error: true, data: null }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attempt]);

  useEffect(() => {
    const el = tableRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => entries.forEach((e) => setTableHeight(e.contentRect.height)));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const columns = useMemo(() => {
    const col = (name, render) => ({
      name, label: t(`performance.reportCenter.audit.col.${name}`), options: { filter: false, sort: false, customBodyRender: render },
    });
    return [
      col('startedAt', (v) => (v ? formatDateTime(v, lang) : '-')),
      col('actorName', (v) => v || t('performance.reportCenter.audit.unknownActor')),
      col('actorScope', (v) => enumLabel(t, 'actorScope', v)),
      col('templateKey', (v) => enumLabel(t, 'reportTemplate', v)),
      col('format', (v) => enumLabel(t, 'format', v)),
      col('periodName', (v) => v || '-'),
      col('status', (v) => enumLabel(t, 'runStatus', v)),
      col('rowCount', (v) => formatNumber(v, lang, 0)),
      col('durationMs', (v) => formatNumber(v, lang, 0)),
      col('filterJson', (v) => filterSummary(t, v) || '-'),
    ];
  }, [t, lang]);

  const onTableChanged = useCallback((action, state) => {
    setPage({ pageNumber: (state.page || 0) + 1, pageSize: state.rowsPerPage || 20 });
  }, []);

  const s = summary.data;
  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v || null }));
  const setDate = (k) => (v, ctx) => { if (!ctx?.validationError) setFilters((f) => ({ ...f, [k]: v || '' })); };

  return (
    <>
      <Fixed>
        <Row data-section="audit-filters">
          <Field $w="300px">
            <GenericSelect
              label={t('performance.reportCenter.audit.template')}
              value={filters.template}
              onChange={set('template')}
              options={extraOptions(t, 'template[eq]', templates)}
              placeholder={t('performance.reportCenter.any')}
            />
          </Field>
          <Field $w="160px">
            <GenericSelect
              label={t('performance.reportCenter.audit.format')}
              value={filters.format}
              onChange={set('format')}
              options={extraOptions(t, 'format[eq]')}
              placeholder={t('performance.reportCenter.any')}
            />
          </Field>
          <Field $w="200px">
            <GenericSelect
              label={t('performance.reportCenter.audit.status')}
              value={filters.status}
              onChange={set('status')}
              options={extraOptions(t, 'runStatus[eq]')}
              placeholder={t('performance.reportCenter.any')}
            />
          </Field>
          <Field $w="180px">
            <DateTimePicker label={t('performance.reportCenter.dateFrom')} mode="date" value={filters.from} onChange={setDate('from')} />
          </Field>
          <Field $w="180px">
            <DateTimePicker label={t('performance.reportCenter.dateTo')} mode="date" value={filters.to} onChange={setDate('to')} />
          </Field>
          <Button variant="ghost" onClick={() => setFilters(EMPTY)} data-action="reset-audit">{t('performance.reportCenter.reset')}</Button>
        </Row>
        <Kpis data-section="run-summary">
          <Kpi data-metric="M58">
            <KpiLabel>{t('performance.reportCenter.metric.M58')}</KpiLabel>
            <KpiValue>
              {summary.loading ? t('common.loading')
                : summary.error ? t('performance.reportCenter.loadFailed')
                  : s?.successRate == null ? t('performance.reportCenter.noData') : `${formatNumber(s.successRate, lang)}%`}
            </KpiValue>
            {s ? <KpiSub>{t('performance.reportCenter.ratio', { num: formatNumber(s.succeeded, lang, 0), den: formatNumber(s.total, lang, 0) })}</KpiSub> : null}
          </Kpi>
          <Kpi data-metric="M59">
            <KpiLabel>{t('performance.reportCenter.metric.M59')}</KpiLabel>
            <KpiValue>
              {summary.loading ? t('common.loading')
                : summary.error ? t('performance.reportCenter.loadFailed')
                  : s?.p95DurationMs == null ? t('performance.reportCenter.noData') : t('performance.reportCenter.audit.ms', { value: formatNumber(s.p95DurationMs, lang, 0) })}
            </KpiValue>
          </Kpi>
        </Kpis>
        <Meta>{t('performance.reportCenter.audit.systemNote')}</Meta>
      </Fixed>
      <TableArea ref={tableRef}>
        {runs.error ? (
          <StateBox data-state="runs-error">
            <span>{t('performance.reportCenter.loadFailed')}</span>
            <Button variant="outline" onClick={() => setAttempt((a) => a + 1)} data-action="retry-runs">{t('performance.reportCenter.retry')}</Button>
          </StateBox>
        ) : (
          <DataTable
            title={t('performance.reportCenter.audit.title')}
            columns={columns}
            data={runs.data?.data || []}
            fetching={runs.loading}
            serverSide
            totalRowCount={runs.data?.totalRowCount || 0}
            pageIndex={page.pageNumber - 1}
            pageSize={page.pageSize}
            onTableChanged={onTableChanged}
            disableFilters
            canCreateNewRecord={false}
            tableBodyMaxHeight={narrow ? 'none' : `${Math.max(tableHeight - 170, 160)}px`}
          />
        )}
      </TableArea>
    </>
  );
}
